import type { MaybeRefOrGetter } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import { createEventHook, createSharedComposable, tryOnScopeDispose } from '@vueuse/core';
import { computed, nextTick, shallowRef, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { showConfirmationDialog } from '#layers/ui/utils/show-confirmation-dialog';
import { useNavigationGuard } from './use-navigation-guard';

type GuardDestination = (to: RouteLocationNormalized) => boolean;

interface EditGuardTracker {
	hasChanges: MaybeRefOrGetter<boolean>;
	shouldGuard?: GuardDestination;
}

/**
 * @internal
 */
export function useGlobalEditGuard() {
	const discardHook = createEventHook();

	const { t } = useI18n();
	const router = useRouter();
	let skipGuard = false;
	let dialogIsOpen = false;

	// Tracked as an immutable list swapped on add/remove, so a `shallowRef` keeps each tracker a raw
	// object: a deep `ref` would proxy them and break the identity check the disposer relies on.
	const trackers = shallowRef<EditGuardTracker[]>([]);
	const hasChanges = computed(() => trackers.value.some((tracker) => toValue(tracker.hasChanges)));

	// Only prompt when the path changes. A query- or hash-only navigation is not a page exit, so the
	// surface driving it owns its state across the change: the settings overlay swaps
	// `<component :is="panel">` on `?settings=`, and its forms survive that swap only because they
	// are `auto-save` and `SystemForm` flushes in `onBeforeUnmount`.
	//
	// Then prompt only for a dirty tracker that also wants to guard this destination. A tracker
	// without a `shouldGuard` guards every destination, so callers that pass none keep prompting on
	// any change.
	const shouldPrompt = (to: RouteLocationNormalized, from: RouteLocationNormalized) =>
		to.path !== from.path
		&& trackers.value.some((tracker) => toValue(tracker.hasChanges) && (tracker.shouldGuard?.(to) ?? true));

	const trackHasChanges = (getter: MaybeRefOrGetter<boolean>, shouldGuard?: GuardDestination) => {
		const tracker: EditGuardTracker = { hasChanges: getter, shouldGuard };
		trackers.value = [...trackers.value, tracker];
		return () => trackers.value = trackers.value.filter((entry) => entry !== tracker);
	};

	useNavigationGuard(
		hasChanges,
		(to, from) => {
			if (!skipGuard && shouldPrompt(to, from)) {
				nextTick(async () => {
					if (!dialogIsOpen) {
						dialogIsOpen = true;
						const keepEditing = await showConfirmationDialog({
							props: {
								title: t('navigation_guard.unsaved_changes.title'),
								description: t('navigation_guard.unsaved_changes.description'),
								confirm: {
									label: t('navigation_guard.unsaved_changes.keep_editing'),
								},
								cancel: {
									label: t('navigation_guard.unsaved_changes.discard'),
								},
								close: false,
								dismissible: false,
							},
						});

						if (!keepEditing) {
							discardHook.trigger();
							skipGuard = true;

							router.push(to);
						}

						dialogIsOpen = false;
					}
				});

				return false;
			}

			skipGuard = false;
		},
	);

	return {
		onDiscard: discardHook.on,
		trackHasChanges,
	};
}

const useSharedGlobalEditGuard = createSharedComposable(useGlobalEditGuard);

export function useEditGuard(
	hasChanges: MaybeRefOrGetter<boolean>,
	options?: { onDiscard?: () => void; shouldGuard?: GuardDestination },
) {
	const { onDiscard, trackHasChanges } = useSharedGlobalEditGuard();

	const disposeTracker = trackHasChanges(hasChanges, options?.shouldGuard);
	const { off: disposeDiscardListener } = onDiscard(() => options?.onDiscard?.());

	const dispose = () => {
		disposeTracker();
		disposeDiscardListener?.();
	};

	tryOnScopeDispose(dispose);
}
